// ============================================================================
// BESPOKIAN Health - Login Page
// Secure authentication with luxury BESPOKIAN styling
// ============================================================================

import { useState, useRef } from 'react';
import { useNavigate, Link } from '@tanstack/react-router';
import { Eye, EyeOff, AlertCircle, Lock } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Button, Card, CardContent, Alert, Spinner } from '@/components/common/ui';

// ============================================================================
// Component
// ============================================================================

export function LoginPage() {
  const navigate = useNavigate();
  const { login, isLoading: authLoading } = useAuth();

  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    const email = emailRef.current?.value || '';
    const password = passwordRef.current?.value || '';

    try {
      await login(email, password);
      navigate({ to: '/dashboard' });
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : typeof err === 'object' && err !== null && 'message' in err && typeof (err as { message: unknown }).message === 'string'
            ? (err as { message: string }).message
            : 'Invalid credentials. Please try again.';

      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-navy-50">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-navy-50 via-white to-gold-50/30 px-4">
      <div className="w-full max-w-md">
        {/* Logo & Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-xl mb-4 shadow-card border border-navy-100">
              <img
                src="/logo.svg"
                alt="BESPOKIAN Health"
                className="h-10 w-10 object-contain"
              />
            </div>
          </Link>
          <div className="w-12 h-0.5 bg-gold-500 mx-auto mb-4" />
          <h1 className="font-display text-2xl text-navy-800 tracking-tight italic">Welcome Back</h1>
          <p className="text-navy-500 mt-1">Sign in to your account</p>
        </div>

        {/* Login Card */}
        <Card className="shadow-card border-navy-100 overflow-hidden">
          <div className="h-1 bg-gradient-to-r from-gold-400 via-gold-500 to-gold-400" />
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Error Alert */}
              {error && (
                <Alert variant="danger">
                  <AlertCircle className="h-4 w-4" />
                  <span>{error}</span>
                </Alert>
              )}

              {/* Email Field */}
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-navy-700 mb-1.5"
                >
                  Email Address
                </label>
                <input
                  ref={emailRef}
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  required
                  autoComplete="email"
                  className="w-full px-4 py-2.5 border border-navy-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold-500 focus:border-transparent transition-shadow bg-white text-navy-800 placeholder:text-navy-300"
                />
              </div>

              {/* Password Field */}
              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-navy-700 mb-1.5"
                >
                  Password
                </label>
                <div className="relative">
                  <input
                    ref={passwordRef}
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    required
                    autoComplete="current-password"
                    className="w-full px-4 py-2.5 pr-10 border border-navy-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold-500 focus:border-transparent transition-shadow bg-white text-navy-800 placeholder:text-navy-300"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-navy-400 hover:text-navy-600"
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>

              {/* Remember Me & Forgot Password */}
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-navy-300 text-gold-600 focus:ring-gold-500"
                  />
                  <span className="text-sm text-navy-600">Remember me</span>
                </label>
                <a
                  href="mailto:support@bespokianhealth.com?subject=Password%20Reset%20Request"
                  className="text-sm text-gold-600 hover:text-gold-700 font-medium"
                >
                  Forgot password?
                </a>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                variant="primary"
                className="w-full py-2.5 bg-gold-500 hover:bg-gold-400 text-navy-800 font-semibold"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Spinner size="sm" className="mr-2" />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>

            {/* Register Link */}
            <div className="mt-6 pt-6 border-t border-navy-100 text-center">
              <p className="text-navy-600 text-sm">
                New to BESPOKIAN Health?{' '}
                <Link to="/register" className="text-gold-600 hover:text-gold-700 font-medium">
                  Create an account
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Security Notice */}
        <div className="mt-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-sage-50 rounded-lg text-sm text-sage-700 border border-sage-200">
            <Lock className="h-4 w-4" />
            <span>256-bit SSL encrypted</span>
          </div>
        </div>

        {/* HIPAA Compliance */}
        <p className="text-center text-xs text-navy-400 mt-6">
          Protected by HIPAA-compliant security.
          <br />
          All access is logged and monitored.
        </p>

        {/* Back to Home */}
        <div className="mt-6 text-center">
          <Link to="/" className="text-sm text-navy-500 hover:text-navy-700">
            &larr; Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}
